import { Button, Card, DatePicker, Descriptions, Form, Input, Message, Space, Table } from "@arco-design/web-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { WarehouseAPI } from "@/apis/material";
import { WorkOrderAPI, WOPOAPI, WoFulfillmentAPI } from "@/apis/manufacturing";
import { InputNumber } from "@/components/Numeric";
import { Number, Currency } from "@/components/Numeric";
import WarehouseSelect from "@/components/WarehouseSelect";
import { formatDate, today } from "@/utils";
import { WOPOStatus, WOPOStatusDef } from "@/enums/manufacturing";
import { EnumLabel } from "@/components/Enum";
import _ from "lodash";

const LocationSelect = WarehouseSelect.LocationSelect;
const FormItem = Form.Item;

export const WoFulfillmentAdd = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const workOrderId = params.workOrderId;
    const [form] = Form.useForm();

    const [info, setInfo] = useState({});
    const [pickOrders, setPickOrders] = useState([]);
    const [fulfillQtys, setFulfillQtys] = useState({});
    const [selectedPickOrderIds, setSelectedPickOrderIds] = useState(() => {
        const poIdsParam = searchParams.get("pickOrderIds");
        return poIdsParam ? poIdsParam.split(",").map(id => parseInt(id, 10)).filter(Boolean) : [];
    });
    const [initialFulfillQty] = useState(() => {
        const qtyParam = searchParams.get("fulfillQty");
        return qtyParam ? parseFloat(qtyParam) : null;
    });
    const [previewData, setPreviewData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [itemWarehouseIds, setItemWarehouseIds] = useState({});

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const [woData, poData, warehouses] = await Promise.all([
                    WorkOrderAPI.get(workOrderId),
                    WOPOAPI.getByWO(workOrderId),
                    WarehouseAPI.actives(true)
                ]);
                setInfo(woData);
                const openPOs = (poData || []).filter(po => po.status === WOPOStatus.Issued || po.status === WOPOStatus.Partial);
                setPickOrders(openPOs);
                // Initialize fulfillQtys: default to remaining qty for each pick order
                // If a fulfillQty param was passed, use it for the pick order that was specified in the URL
                const initialQtys = {};
                const targetPoId = selectedPickOrderIds.length === 1 ? selectedPickOrderIds[0] : null;
                openPOs.forEach(po => {
                    const fulfilledQty = po.fulfilledQty || 0;
                    const remaining = (po.pickQty || 0) - fulfilledQty;
                    if (initialFulfillQty !== null && po.id === targetPoId) {
                        initialQtys[po.id] = Math.min(initialFulfillQty, remaining);
                    } else {
                        initialQtys[po.id] = remaining;
                    }
                });
                setFulfillQtys(initialQtys);
                setWarehouseOptions(_.map(warehouses, w => ({ value: w.id, label: w.warehouseName })));
                form.setFieldsValue({ fulfillDate: today() });
                setDataLoaded(true);
            } catch (error) {
                Message.error(error.message);
            } finally {
                setLoading(false);
            }
        };
        init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workOrderId, form]);

    const getSelectedFulfillQtyTotal = () => {
        return selectedPickOrderIds.reduce((sum, id) => sum + (fulfillQtys[id] || 0), 0);
    };

    // Redistribute the total raw cost to finished items by their value
    // allocation ratio (null allocation falls back to an equal split, same as
    // the backend) so the Finished Items total always matches the Raw Items
    // total.
    const recalcFinishedItems = (finishedItems, totalRawCost) => {
        const list = finishedItems || [];
        if (list.length === 0) return list;
        return list.map(item => {
            const valueAmount = item.valueAllocation != null
                ? totalRawCost * (item.valueAllocation / 100)
                : totalRawCost / list.length;
            const quantity = item.quantity || 0;
            const cost = quantity > 0 ? valueAmount / quantity : 0;
            return { ...item, valueAmount, cost };
        });
    };

    const handleUsedQtyChange = (index, val) => {
        setPreviewData(prev => {
            if (!prev) return prev;
            const rawItems = [...(prev.rawItems || [])];
            const item = rawItems[index];
            if (!item) return prev;
            const usedQty = val || 0;
            rawItems[index] = { ...item, usedQty, valueAmount: usedQty * (item.cost || 0) };
            const totalRawCost = rawItems.reduce((sum, r) => sum + (r.valueAmount || 0), 0);
            return { ...prev, rawItems, finishedItems: recalcFinishedItems(prev.finishedItems, totalRawCost) };
        });
    };

    // The finished quantity is editable; all value amounts stay frozen and the
    // unit cost simply follows Value / Finished Qty
    const handleFinishedQtyChange = (index, val) => {
        setPreviewData(prev => {
            if (!prev) return prev;
            const finishedItems = [...(prev.finishedItems || [])];
            const item = finishedItems[index];
            if (!item) return prev;
            const quantity = val || 0;
            finishedItems[index] = {
                ...item,
                quantity,
                cost: quantity > 0 ? (item.valueAmount || 0) / quantity : 0
            };
            return { ...prev, finishedItems };
        });
    };



    useEffect(() => {
        if (!dataLoaded) return;
        const loadPreview = async () => {
            if (selectedPickOrderIds.length === 0) {
                setPreviewData(null);
                return;
            }
            // Skip the preview while any fulfill qty is invalid (e.g. the input
            // is cleared while typing) to avoid transient backend errors
            if (selectedPickOrderIds.some(id => !(fulfillQtys[id] > 0))) {
                return;
            }
            // Snapshot the user-entered warehouse/location/lot/expiration so
            // they survive preview reloads triggered by fulfill qty changes
            const prevItemsByItemId = new Map(
                (form.getFieldValue("finishedItems") || [])
                    .filter(item => item && item.itemId != null)
                    .map(item => [item.itemId, item])
            );
            try {
                const fulfillQtyMap = {};
                selectedPickOrderIds.forEach(id => {
                    fulfillQtyMap[id] = fulfillQtys[id] || 0;
                });
                // No raw item used qty is sent: the backend rescales every raw
                // item's used qty by the pick order's fulfill/pick qty ratio
                // while keeping its unit cost, then re-allocates the resulting
                // raw total to the finished items (value by value allocation,
                // cost = value / finished qty). This makes the raw values, the
                // finished quantities, values and costs all follow a fulfill
                // qty change.
                const data = await WoFulfillmentAPI.preview({
                    workOrderId: parseInt(workOrderId, 10),
                    pickOrderIds: selectedPickOrderIds,
                    fulfillQtyMap
                });
                setPreviewData({
                    ...data,
                    finishedItems: (data.finishedItems || []).map((item, i) => ({ ...item, _key: `fi-${i}` })),
                    rawItems: (data.rawItems || []).map((item, i) => ({ ...item, _key: `ri-${i}` })),
                });
                const finishedItems = (data.finishedItems || []).map((item, i) => {
                    const prev = prevItemsByItemId.get(item.itemId);
                    return {
                        ...item,
                        _key: `fi-${i}`,
                        warehouseId: (prev && prev.warehouseId != null ? prev.warehouseId : item.warehouseId) || undefined,
                        warehouseName: (prev && prev.warehouseName) || item.warehouseName || "",
                        locationId: (prev && prev.locationId != null ? prev.locationId : item.locationId) || undefined,
                        locationName: (prev && prev.locationName) || item.locationName || "",
                        lot: (prev && prev.lot) || item.lot || "",
                        expiredDate: (prev && prev.expiredDate != null ? prev.expiredDate : item.expiredDate) || undefined
                    };
                });
                const rawItems = (data.rawItems || []).map((item, i) => ({
                    ...item,
                    _key: `ri-${i}`,
                    usedQty: item.usedQty
                }));
                form.setFieldsValue({ finishedItems, rawItems });
            } catch (error) {
                Message.error(error.message);
                // Keep the previous preview and the user-entered values
            }
        };
        loadPreview();
    }, [dataLoaded, selectedPickOrderIds, fulfillQtys, workOrderId, form]);

    const save = async () => {
        if (selectedPickOrderIds.length === 0) {
            Message.warning("Please select at least one pick order");
            return;
        }
        if (saving) return;
        setSaving(true);
        try {
            const values = await form.validate();
            const finishedItems = values.finishedItems || [];
            for (let i = 0; i < finishedItems.length; i++) {
                if (!finishedItems[i].warehouseId) {
                    Message.warning(`Please select a warehouse for finished item #${i + 1}`);
                    setSaving(false);
                    return;
                }
                if (!(finishedItems[i].quantity > 0)) {
                    Message.warning(`Please enter a valid finished quantity for finished item #${i + 1}`);
                    setSaving(false);
                    return;
                }
            }
            const payloadFinishedItems = finishedItems.map(item => ({
                itemId: item.itemId,
                quantity: item.quantity,
                warehouseId: item.warehouseId,
                warehouseName: item.warehouseName,
                locationId: item.locationId,
                locationName: item.locationName,
                lot: item.lot,
                expiredDate: item.expiredDate
            }));
            const rawItems = (values.rawItems || []).map(item => ({
                itemId: item.itemId,
                usedQty: item.usedQty
            }));
            const fulfillQtyMap = {};
            selectedPickOrderIds.forEach(id => {
                fulfillQtyMap[id] = fulfillQtys[id] || 0;
            });
            const resultId = await WoFulfillmentAPI.add({
                workOrderId: parseInt(workOrderId, 10),
                pickOrderIds: selectedPickOrderIds,
                fulfillQtyMap,
                fulfillDate: values.fulfillDate,
                finishedItems: payloadFinishedItems,
                rawItems,
                notes: ""
            });
            Message.success("Fulfillment created successfully");
            navigate(`/workspace/manufacturing/wo-fulfillment/${resultId}`);
        } catch (error) {
            Message.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const pickOrderColumns = [
        {
            title: "Pick Order#", dataIndex: "pickOrderNo",
            render: (val, record) =>
                <Link to={`/workspace/manufacturing/wo-pick-order/${record.id}`}>{val}</Link>
        },
        { title: "Status", dataIndex: "status", render: (col, record) => <EnumLabel enumObj={WOPOStatusDef} value={record.status} /> },
        { title: "Pick Date", dataIndex: "pickDate", render: (val) => val ? formatDate(val) : "" },
        { title: "Warehouse", dataIndex: "warehouseName" },
        { title: "Pick Qty", render: (col, record) => <Number value={record.pickQty} /> },
        { title: "Fulfilled Qty", render: (col, record) => <Number value={record.fulfilledQty || 0} /> },
        {
            title: "Fulfill Qty", width: 140,
            render: (col, record) => (
                <InputNumber
                    style={{ width: "100%" }}
                    min={0.0001}
                    max={(record.pickQty || 0) - (record.fulfilledQty || 0)}
                    precision={4}
                    value={selectedPickOrderIds.includes(record.id) ? (fulfillQtys[record.id] || 0) : undefined}
                    disabled={!selectedPickOrderIds.includes(record.id)}
                    onChange={(val) => {
                        setFulfillQtys(prev => ({ ...prev, [record.id]: val || 0 }));
                    }}
                />
            )
        },
    ];

    return (
        <Card title="Fulfillment Order" extra={
            <Space>
                <Button type="primary" loading={saving} onClick={() => save()}>Save</Button>
                <Button onClick={() => navigate(`/workspace/manufacturing/work-order/${workOrderId}`)}>Back</Button>
            </Space>
        }>
            <Form form={form}>
                <Descriptions colon=":" column={3} data={[
                    { label: "Work Order#", value: info.id ? <Link to={`/workspace/manufacturing/work-order/${info.id}`}>{info.workOrderNo}</Link> : info.workOrderNo },
                    { label: "BOM", value: info.bomId ? <Link to={`/workspace/manufacturing/bom/${info.bomId}`}>{info.bomName}</Link> : info.bomName },
                    { label: "BOM Description", value: info.bomDescription || "" },
                    { label: "Fulfilled Qty", value: <Number value={getSelectedFulfillQtyTotal()} /> },
                    { label: "Fulfillment Date", value: (
                        <FormItem field="fulfillDate" rules={[{ required: true, message: "Please select fulfill date" }]} noStyle>
                            <DatePicker style={{ width: 200 }} />
                        </FormItem>
                    )},
                ]} />

                <div style={{ fontWeight: "bold", margin: "24px 0 8px" }}>Pick Orders</div>
                <Table
                    rowKey="id"
                    loading={loading}
                    rowSelection={{
                        selectedRowKeys: selectedPickOrderIds,
                        onChange: (selectedRowKeys) => setSelectedPickOrderIds(selectedRowKeys),
                    }}
                    columns={pickOrderColumns}
                    data={pickOrders}
                    pagination={false}
                />

                {previewData && (
                    <>
                        <div style={{ fontWeight: "bold", margin: "24px 0 8px" }}>Finished Items</div>
                        <Table
                            rowKey="_key"
                            columns={[
                                {
                                    title: (<div style={{ lineHeight: 1.5 }}><div style={{ fontSize: 14, fontWeight: 600 }}>Item Name</div><div style={{ fontSize: 12, color: '#86909c' }}>Description</div></div>),
                                    dataIndex: "itemName",
                                    render: (col, record) => (
                                        <div style={{ lineHeight: 1.5 }}>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: '#1d2129' }}>{record.itemName || ""}</div>
                                            <div style={{ fontSize: 12, color: '#86909c' }}>{record.description || ""}</div>
                                        </div>
                                    )
                                },
                                { title: "BOM Qty", render: (col, record) => <Number value={record.bomQty} /> },
                                {
                                    title: "Finished Qty", width: 140,
                                    render: (col, record, index) => (
                                        <FormItem field={`finishedItems[${index}].quantity`} noStyle>
                                            <InputNumber
                                                style={{ width: "100%" }}
                                                min={0.0001}
                                                onChange={(val) => handleFinishedQtyChange(index, val)}
                                            />
                                        </FormItem>
                                    )
                                },
                                { title: "UOM", dataIndex: "uom" },
                                { title: "Cost", render: (col, record) => <Currency value={record.cost} /> },
                                { title: "Value", render: (col, record) => <Currency value={record.valueAmount} /> },
                                {
                                    title: "Warehouse", width: 180,
                                    render: (col, record, index) => (
                                        <>
                                            <FormItem field={`finishedItems[${index}].itemId`} hidden><Input /></FormItem>
                                            <FormItem field={`finishedItems[${index}].warehouseName`} hidden><Input /></FormItem>
                                            <FormItem field={`finishedItems[${index}].warehouseId`} noStyle>
                                                <WarehouseSelect
                                                    includeUnavailable
                                                    style={{ width: "100%" }}
                                                    onChange={(val) => {
                                                        const warehouse = _.find(warehouseOptions, o => o.value === val);
                                                        form.setFieldValue(`finishedItems[${index}].warehouseName`, warehouse ? warehouse.label : "");
                                                        form.setFieldValue(`finishedItems[${index}].locationId`, undefined);
                                                        form.setFieldValue(`finishedItems[${index}].locationName`, undefined);
                                                        setItemWarehouseIds(prev => ({ ...prev, [index]: val }));
                                                    }}
                                                />
                                            </FormItem>
                                        </>
                                    )
                                },
                                {
                                    title: "Location", width: 150,
                                    render: (col, record, index) => (
                                        <>
                                            <FormItem field={`finishedItems[${index}].locationName`} hidden><Input /></FormItem>
                                            <FormItem field={`finishedItems[${index}].locationId`} noStyle>
                                                <LocationSelect
                                                    warehouseId={itemWarehouseIds[index] !== undefined ? itemWarehouseIds[index] : form.getFieldValue(`finishedItems[${index}].warehouseId`)}
                                                    onlyActive={false}
                                                    style={{ width: "100%" }}
                                                    onChange={(val, label) => {
                                                        form.setFieldValue(`finishedItems[${index}].locationName`, label || "");
                                                    }}
                                                />
                                            </FormItem>
                                        </>
                                    )
                                },
                                {
                                    title: "Lot#", width: 160,
                                    render: (col, record, index) => (
                                        <FormItem field={`finishedItems[${index}].lot`} noStyle>
                                            <Input />
                                        </FormItem>
                                    )
                                },
                                {
                                    title: "Expiration", width: 140,
                                    render: (col, record, index) => (
                                        <FormItem field={`finishedItems[${index}].expiredDate`} noStyle>
                                            <DatePicker style={{ width: "100%" }} />
                                        </FormItem>
                                    )
                                },
                            ]}
                            data={previewData.finishedItems || []}
                            pagination={false}
                            summary={(currentData) => {
                                const totalValue = (currentData || []).reduce((sum, r) => sum + (r.valueAmount || 0), 0);
                                return (
                                    <Table.Summary>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell>Total</Table.Summary.Cell>
                                            <Table.Summary.Cell />
                                            <Table.Summary.Cell />
                                            <Table.Summary.Cell />
                                            <Table.Summary.Cell />
                                            <Table.Summary.Cell><Currency value={totalValue} /></Table.Summary.Cell>
                                            <Table.Summary.Cell />
                                            <Table.Summary.Cell />
                                            <Table.Summary.Cell />
                                            <Table.Summary.Cell />
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                );
                            }}
                        />

                        <div style={{ fontWeight: "bold", margin: "24px 0 8px" }}>Raw Items</div>
                        <Table
                            rowKey="_key"
                            columns={[
                                {
                                    title: (<div style={{ lineHeight: 1.5 }}><div style={{ fontSize: 14, fontWeight: 600 }}>Item Name</div><div style={{ fontSize: 12, color: '#86909c' }}>Description</div></div>),
                                    dataIndex: "itemName",
                                    render: (col, record) => (
                                        <div style={{ lineHeight: 1.5 }}>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: '#1d2129' }}>{record.itemName || ""}</div>
                                            <div style={{ fontSize: 12, color: '#86909c' }}>{record.description || ""}</div>
                                        </div>
                                    )
                                },
                                { title: "Pick Qty", render: (col, record) => <Number value={record.pickQty} /> },
                                {
                                    title: "Used Qty", width: 120,
                                    render: (col, record, index) => (
                                        <>
                                            <FormItem field={`rawItems[${index}].itemId`} hidden><Input /></FormItem>
                                            <FormItem field={`rawItems[${index}].usedQty`} noStyle>
                                                <InputNumber style={{ width: "100%" }} min={0} max={record.pickQty} onChange={(val) => handleUsedQtyChange(index, val)} />
                                            </FormItem>
                                        </>
                                    )
                                },
                                { title: "UOM", dataIndex: "uom" },
                                { title: "Cost", render: (col, record) => <Currency value={record.cost} /> },
                                { title: "Value", render: (col, record) => <Currency value={record.valueAmount} /> },
                                {
                                    title: "Warehouse Location",
                                    render: (col, record) => {
                                        const parts = [record.warehouseName, record.locationName].filter(Boolean);
                                        return parts.join(" / ") || "-";
                                    }
                                },
                                { title: "Lot#", dataIndex: "lot" },
                                { title: "Expiration", dataIndex: "expiredDate" },
                            ]}
                            data={previewData.rawItems || []}
                            pagination={false}
                            summary={(currentData) => {
                                const totalValue = (currentData || []).reduce((sum, r) => sum + (r.valueAmount || 0), 0);
                                return (
                                    <Table.Summary>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell>Total</Table.Summary.Cell>
                                            <Table.Summary.Cell />
                                            <Table.Summary.Cell />
                                            <Table.Summary.Cell />
                                            <Table.Summary.Cell />
                                            <Table.Summary.Cell><Currency value={totalValue} /></Table.Summary.Cell>
                                            <Table.Summary.Cell />
                                            <Table.Summary.Cell />
                                            <Table.Summary.Cell />
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                );
                            }}
                        />
                    </>
                )}
            </Form>
        </Card>
    );
};
